import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { useGradientBackground } from '@/utils/gradientBackground';

export default function ServicesMeetAdamSection() {
  // Parallax for subtitle
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (subtle)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const leftColumnVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' as const },
    },
  };

  const rightColumnVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' as const, delay: 0.05 },
    },
  };

  const quickFacts = [
    "Works specifically with consultants, small firms, and micro-agencies.",
    "Obsessed with domains, positioning, and how they translate into booked calls.",
    "Blends brand, UX, copy, and tech so you don't have to juggle 3–4 different freelancers.",
    "Prefers clear plans, honest feedback, and shipping things you'll actually use.",
  ];

  const hiringForBullets = [
    "Translation from \"what you do\" into clear offers and positioning. Turning your experience and niche into language and structure that clicks for the right clients.",
    "A brand and website that feel like you — but sharper. No generic template look, no jargon soup, and no design just for design's sake.",
    "A simple, realistic client-getting system. Forms, booking, and follow-ups that you can actually use without a full-time marketing team.",
    "A guided process that respects your time. Clear phases, review points, and deadlines so this doesn't become another open-ended project.",
  ];

  const approachBadges = [
    'Strategy-first',
    'Hands-on build partner',
    'Long-term asset mindset',
  ];

  const backgroundStyle = useGradientBackground();

  return (
    <section className="relative w-full text-slate-900 py-16 md:py-24" style={backgroundStyle}>
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            ref={sectionReveal.ref}
            variants={containerVariants}
            initial="hidden"
            animate={sectionReveal.controls}
          >
            {/* Heading Section */}
            <div className="text-center mb-12 md:mb-16">
              <h2
                ref={titleRef as any}
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6"
              >
                Meet Adam, behind Attain Domains
              </h2>
              <p
                ref={subtitleRef as any}
                className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto"
              >
                I'm a solo founder who's spent years thinking about how domains, brands, and websites actually work together to bring in better clients — not just how they look on a moodboard.
              </p>
            </div>

            {/* Two-Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              {/* Left Column: Photo + Quick Facts */}
              <motion.div variants={leftColumnVariants} className="space-y-6">
                {/* Headshot */}
                <div className="flex justify-center lg:justify-start">
                  <div className="relative w-full max-w-[260px] aspect-square overflow-hidden rounded-2xl">
                    <img
                      src="/media/services/adam-portrait.jpg"
                      alt="Portrait of Adam, founder of Attain Domains"
                      className="w-full h-full object-cover grayscale opacity-95 hover:grayscale-0 hover:opacity-100 hover:scale-[1.01] transition-all duration-300 ease-out"
                    />
                  </div>
                </div>

                {/* Quick Facts */}
                <div>
                  <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase mb-3">
                    Quick facts
                  </h3>
                  <ul className="space-y-3">
                    {quickFacts.map((fact, index) => (
                      <li key={index} className="flex items-start gap-2 text-slate-800 text-sm leading-relaxed">
                        <span className="text-blue-600 mt-1.5 shrink-0">•</span>
                        <span>{fact}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Approach Badges */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {approachBadges.map((badge, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded-full"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Right Column: Bio + Bullets */}
              <motion.div variants={rightColumnVariants} className="lg:col-span-2 space-y-8">
                {/* Intro Paragraph */}
                <div>
                  <p className="text-slate-800 leading-relaxed">
                    If you've ever felt like your domain should be doing more for your business, that's the space I live in. I work with people who sell expertise — consultants, small firms, and micro-agencies — to turn strong domains into clear brands, focused websites, and simple systems for bringing in better clients.
                  </p>
                </div>

                {/* What You're Hiring Me For */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase mb-4">
                    What you're really hiring me for
                  </h3>
                  <ul className="space-y-4">
                    {hiringForBullets.map((bullet, index) => (
                      <li key={index} className="flex items-start gap-3 text-slate-800 leading-relaxed">
                        <span className="text-blue-600 mt-1.5 shrink-0 font-semibold">•</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* What It's Like to Work Together */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase mb-4">
                    What it's like to work together
                  </h3>
                  <div className="space-y-4 text-slate-800 leading-relaxed">
                    <p>
                      I won't drown you in design jargon or ask you to write a 40-page brief. We start with a focused conversation about your domain, your services, and your clients. From there, I turn that into structure, copy, and design — and you give feedback at key checkpoints instead of micromanaging every pixel.
                    </p>
                    <p>
                      My goal is that when we're done, you have something you're proud to send prospects, and a simple plan for how to use it to bring in better-fit work over the next 30–60 days.
                    </p>
                  </div>
                </div>

                {/* Optional Bottom Link */}
                <div className="border-t border-slate-200 pt-6">
                  <p className="text-slate-700">
                    If this sounds like the kind of partner you want, the next step is to{' '}
                    <Link
                      to="/services/consult"
                      className="text-blue-600 hover:text-blue-700 hover:underline font-medium transition-colors duration-200"
                    >
                      book a free 1-hour consult
                    </Link>
                    .
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

